import { DateTime } from '@ksyos/date-time';
import { Text } from '@ksyos/design-system';
import { useNavigate, useParams, useSearchParams } from '@ksyos/router';
import { useEffect, useState } from 'react';
import type { AddEdit } from '@common/utils/utilityTypes';
import type { PayableServiceActivity } from '@common/validation/payableServiceActivity';
import type { ValidationErrors } from '@frontend/utils';
import { ManagerAuthorization } from '@common/enums';
import { getLanguage } from '@common/translate/translate';
import {
    type CompensationPayoutForServiceActivityUpdatableFields,
    compensationPayoutUpdateSchema,
} from '@common/validation/compensationPayoutForServiceActivity';
import {
    createServiceActivityCompensationPayout,
    getOneServiceActivityCompensationPayout,
    updateOneServiceActivityCompensationPayout,
} from '@frontend/services/api/compensationPayoutApiService';
import { getAllPayableActivities } from '@frontend/services/api/payableServiceActivityApiService';
import { getOne as getOneServiceGroupSetting } from '@frontend/services/api/serviceGroupSettingsApiService';
import { getOne as getOneServiceOrganizationSetting } from '@frontend/services/api/serviceOrganizationSettingApiService';
import {
    assertHasAuthorization,
    currencyFormatter,
    emptyValidationErrors,
    hasOneAuthorization,
    OverlapChecker,
    RateLimiter,
    updateValidationErrorsForMany,
    updateValidationErrorsForOne,
    validate,
} from '@frontend/utils';
import { useAbortSignal } from '@frontend/utils/hooks/useAbortSignal';
import type { IBreadcrumbItem } from '../../elements/common';
import {
    Button,
    DateInput,
    Dropdown,
    FormContainer,
    FormGroup,
    FormHeader,
    FormLabel,
    Header,
    InputCurrency,
    InputGroup,
    Loading,
    OverlapMessage,
} from '../../elements/common';
import { FixedWidthTemplate } from '../../templates/FixedWidthTemplate';
import { getUrlWithParams } from './utils/getServiceActivityCompensationPayoutUrl';

type CheckOverlapParams = {
    serviceActivityId: string;
    serviceGroupSettingId?: string;
    serviceOrganizationSettingId?: string;
};

type Overlap = {
    endDate: string | null;
    id: string;
    startDate: string;
};

// @TODO CS-24078 change to use new design system components
export function ServiceActivityCompensationPayoutAddEdit() {
    assertHasAuthorization([
        ManagerAuthorization.groupAddEdit,
        ManagerAuthorization.organizationRead,
        ManagerAuthorization.serviceRightsCareproviders,
        ManagerAuthorization.serviceRightsGroups,
        ManagerAuthorization.serviceRightsOrganizations,
    ]);

    const atMostOncePerSecond = new RateLimiter(1000);
    const overlapChecker = new OverlapChecker('/api/finance/compensationPayouts');
    const abortSignal = useAbortSignal();
    const navigate = useNavigate();
    const translate = getLanguage();
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const serviceGroupSettingId = searchParams.getOptional('serviceGroupSettingId');
    const serviceId = searchParams.getOptional('serviceId');
    const groupId = searchParams.getOptional('groupId');
    const serviceOrganizationSettingId = searchParams.getOptional('serviceOrganizationSettingId');
    const organizationId = searchParams.getOptional('organizationId');

    const [compensationPayout, setCompensationPayout] =
        useState<AddEdit<CompensationPayoutForServiceActivityUpdatableFields>>();
    const [overlap, setOverlap] = useState<Overlap | undefined>();
    const [validationErrors, setValidationErrors] = useState<ValidationErrors>(emptyValidationErrors);
    const [isSaving, setIsSaving] = useState<boolean>(false);
    const [right, setRight] = useState<{
        endDate: string | null;
        name: string;
        service: { name: string };
        startDate: string;
    }>();
    const [payableServiceActivities, setPayableServiceActivities] = useState<PayableServiceActivity[]>([]);

    const canEdit =
        (serviceOrganizationSettingId &&
            hasOneAuthorization([
                ManagerAuthorization.organizationAddEdit,
                ManagerAuthorization.serviceRightsOrganizations,
            ])) ||
        (serviceGroupSettingId &&
            hasOneAuthorization([ManagerAuthorization.groupAddEdit, ManagerAuthorization.serviceRightsGroups]));

    useEffect(() => {
        setup();
    }, [id]);

    const headerText =
        (isEdit(id) ? translate.common.edit : translate.common.new) + ' ' + translate.compensationPayouts.fees;

    if (!right) {
        return null;
    }

    return (
        <FixedWidthTemplate breadcrumb={getBreadcrumb()}>
            <Header content={headerText} />

            <Loading
                item={compensationPayout}
                loaded={(compensation) => (
                    <FormContainer onSubmit={async () => await save(right.startDate, right.endDate)}>
                        <FormHeader>
                            {`${headerText}: ${right.service.name} ${translate.common.for} ${right.name}`}
                        </FormHeader>

                        {overlap && (
                            <OverlapMessage
                                endDate={overlap.endDate}
                                link={getCompensationPayoutEditUrl(overlap.id)}
                                message={translate.common.overlapMessage}
                                startDate={overlap.startDate}
                            />
                        )}
                        <FormGroup hasErrors={validationErrors.has('startDate')}>
                            <FormLabel isRequired>{translate.common.startDate}</FormLabel>
                            <InputGroup>
                                {!canEdit ? (
                                    renderText(compensation.startDate)
                                ) : (
                                    <DateInput
                                        error={validationErrors.get('startDate')}
                                        maxDate={right?.endDate}
                                        minDate={right?.startDate}
                                        onChange={async (value) =>
                                            await handleChange('startDate', value ?? '', right.startDate, right.endDate)
                                        }
                                        value={compensation.startDate}
                                    />
                                )}
                            </InputGroup>
                        </FormGroup>

                        <FormGroup hasErrors={validationErrors.has('endDate')}>
                            <FormLabel>{translate.common.endDate}</FormLabel>
                            <InputGroup>
                                {!canEdit ? (
                                    renderText(compensation.endDate)
                                ) : (
                                    <DateInput
                                        error={validationErrors.get('endDate')}
                                        maxDate={right?.endDate}
                                        minDate={right?.startDate}
                                        onChange={async (value) =>
                                            await handleChange('endDate', value, right.startDate, right.endDate)
                                        }
                                        value={compensation.endDate}
                                    />
                                )}
                            </InputGroup>
                        </FormGroup>

                        <FormGroup hasErrors={validationErrors.has('amount')}>
                            <FormLabel isRequired>{translate.compensationPayouts.amount}</FormLabel>
                            <InputGroup>
                                {!canEdit ? (
                                    renderText(compensation.amount ? currencyFormatter(compensation.amount / 100) : '')
                                ) : (
                                    <InputCurrency
                                        error={validationErrors.get('amount')}
                                        onChange={async (value) =>
                                            handleChange('amount', value, right.startDate, right.endDate)
                                        }
                                        value={compensation.amount}
                                    />
                                )}
                            </InputGroup>
                        </FormGroup>

                        <FormGroup hasErrors={validationErrors.has('serviceActivityId')}>
                            <FormLabel isRequired>{translate.common.activity}</FormLabel>
                            <InputGroup>
                                {!canEdit ? (
                                    renderText(compensation.serviceActivityId)
                                ) : (
                                    <Dropdown
                                        error={validationErrors.get('serviceActivityId')}
                                        onChange={async (value) =>
                                            await handleChange(
                                                'serviceActivityId',
                                                value,
                                                right.startDate,
                                                right.endDate
                                            )
                                        }
                                        selectOptions={payableServiceActivities.map((activity) => {
                                            return {
                                                text: activity.serviceActivity.name,
                                                value: activity.serviceActivityId,
                                            };
                                        })}
                                        value={compensation.serviceActivityId}
                                    />
                                )}
                            </InputGroup>
                        </FormGroup>

                        <Button
                            color="secondary"
                            disabled={isSaving}
                            large
                            left
                            onClick={() => navigate(-1)}
                            outlined
                        >
                            {canEdit ? translate.common.cancel : translate.common.goBack}
                        </Button>
                        {canEdit && (
                            <Button
                                automation="saveSubmit"
                                color="secondary"
                                disabled={isSaving || !!overlap}
                                large
                                submit
                            >
                                {translate.common.save}
                            </Button>
                        )}
                    </FormContainer>
                )}
            />
        </FixedWidthTemplate>
    );

    async function loadCompensationPayout(compensationPayoutId: string) {
        const result = await getOneServiceActivityCompensationPayout(compensationPayoutId, abortSignal);

        if (result) {
            checkOverlap(result);
            setCompensationPayout(result);
        }
    }

    function isEdit(compensationPayoutId?: string): compensationPayoutId is string {
        return !!compensationPayoutId;
    }

    async function setup() {
        setIsSaving(false);
        const payableActivities = await getAllPayableActivities({ isActive: true, serviceId }, abortSignal);
        setPayableServiceActivities(payableActivities);

        if (serviceGroupSettingId) {
            loadRight('serviceGroupSettings', serviceGroupSettingId);
        } else if (serviceOrganizationSettingId) {
            loadRight('serviceOrganizationSettings', serviceOrganizationSettingId);
        }
        if (id) {
            await loadCompensationPayout(id);
        } else {
            const newCompensation = {
                amount: 0,
                endDate: null,
                serviceActivityId: payableActivities[0]?.serviceActivityId,
                serviceGroupSettingId: serviceGroupSettingId ?? null,
                serviceOrganizationSettingId: serviceOrganizationSettingId ?? null,
                startDate: DateTime.now().format('YYYY-MM-DD'),
            };
            await checkOverlap(newCompensation);
            setCompensationPayout(newCompensation);
        }
    }

    async function handleChange<K extends keyof AddEdit<CompensationPayoutForServiceActivityUpdatableFields>>(
        name: K,
        value: AddEdit<CompensationPayoutForServiceActivityUpdatableFields>[K],
        minStartDate: string,
        maxEndDate: string | null
    ) {
        if (compensationPayout) {
            const newCompensationPayout = { ...compensationPayout, [name]: value };

            let errors = validationErrors;
            if (name === 'startDate') {
                errors = updateValidationErrorsForMany(
                    compensationPayoutUpdateSchema(minStartDate, maxEndDate),
                    newCompensationPayout,
                    validationErrors,
                    ['startDate', 'endDate']
                );
            } else {
                errors = updateValidationErrorsForOne(
                    compensationPayoutUpdateSchema(minStartDate, maxEndDate),
                    newCompensationPayout,
                    validationErrors,
                    name
                );
            }

            setValidationErrors(errors);
            if (!errors.size) {
                await checkOverlap(newCompensationPayout);
            }

            setCompensationPayout(newCompensationPayout);
        }
    }

    async function checkOverlap(compensation: AddEdit<CompensationPayoutForServiceActivityUpdatableFields>) {
        if (!compensation.startDate || !compensation.serviceActivityId) {
            return;
        }

        atMostOncePerSecond.callMeMaybe(async () => {
            const checkParams: CheckOverlapParams = {
                serviceActivityId: compensation.serviceActivityId!,
            };

            if (serviceOrganizationSettingId) {
                checkParams.serviceOrganizationSettingId = serviceOrganizationSettingId;
            } else if (serviceGroupSettingId) {
                checkParams.serviceGroupSettingId = serviceGroupSettingId;
            }

            const isOverlapped = await overlapChecker.check(
                checkParams,
                compensation.startDate,
                compensation.endDate,
                id
            );
            setOverlap(isOverlapped);
        });
    }

    async function save(minStartDate: string, maxEndDate: string | null) {
        if (overlap) {
            return;
        }

        const { errors, item } = validate(compensationPayoutUpdateSchema(minStartDate, maxEndDate), compensationPayout);

        setValidationErrors(errors);
        if (errors.size > 0 || !item) {
            return;
        }

        setIsSaving(true);

        if (isEdit(id)) {
            await updateOneServiceActivityCompensationPayout(item, id, abortSignal);
        } else {
            await createServiceActivityCompensationPayout(
                item,
                {
                    serviceGroupSettingId,
                    serviceOrganizationSettingId,
                },
                abortSignal
            );
        }

        setIsSaving(false);

        navigate(-1);
    }

    function getCompensationPayoutEditUrl(compensationId: string) {
        return getUrlWithParams(`/serviceActivityCompensationPayout/${compensationId}`, {
            groupId,
            organizationId,
            serviceGroupSettingId,
            serviceId,
            serviceOrganizationSettingId,
        });
    }

    async function loadRight(name: 'serviceGroupSettings' | 'serviceOrganizationSettings', rightId: string) {
        const isGroupSetting = name === 'serviceGroupSettings';
        const result = isGroupSetting
            ? await getOneServiceGroupSetting(rightId, abortSignal)
            : await getOneServiceOrganizationSetting(rightId, abortSignal);

        setRight({
            endDate: result.json.endDate,
            name: isGroupSetting ? result.json.group.name : result.json.organization.name,
            service: { name: result.json.service.name },
            startDate: result.json.startDate,
        });
    }

    function getBreadcrumb() {
        let breadcrumb: IBreadcrumbItem[] = [];

        if (serviceGroupSettingId && right) {
            if (groupId) {
                const returnUrl = `/groups/${groupId}`;
                breadcrumb = [
                    { text: right.name, url: returnUrl },
                    { text: right.service.name, url: returnUrl },
                ];
            } else if (serviceId) {
                const returnUrl = `/services/${serviceId}?tab=groupSettings`;
                breadcrumb = [
                    { text: right.service.name, url: returnUrl },
                    { text: right.name, url: returnUrl },
                ];
            }
        } else if (serviceOrganizationSettingId && right) {
            if (serviceId) {
                const returnUrl = `/services/${serviceId}?tab=providerOrganizationSettings`;
                breadcrumb = [
                    { text: right.service.name, url: returnUrl },
                    { text: right.name, url: returnUrl },
                ];
            } else if (organizationId) {
                const returnUrl = `/organizations/${organizationId}?tab=serviceOrganizationSettings`;
                breadcrumb = [
                    { text: right.name, url: returnUrl },
                    { text: right.service.name, url: returnUrl },
                ];
            }
        }

        breadcrumb.push({
            text: headerText,
        });

        return breadcrumb;
    }

    function renderText(content: string | null) {
        return (
            <Text
                as="p"
                fontWeight="medium"
            >
                {content}
            </Text>
        );
    }
}
